// src/api/logosSocietes.api.ts — SCE Issues
// Le logo d'une société (29/10) : un fichier PNG dans la bibliothèque des pièces
// jointes de l'appli (`_SCE Issues/Logos des sociétés/`), comme le cycle de vie,
// pas un bloc de `__global__` (lue au démarrage par tout le monde). La société
// garde l'identifiant du fichier (roles.societes[].logo) : un nouveau logo est
// un nouveau fichier, l'ancien reste dans la bibliothèque.
//
// Réduit dans le navigateur avant l'envoi (256 px au plus, PNG transparent) :
// quelques dizaines de ko, quelle que soit l'image donnée. Relu PAR GRAPH (lien
// de téléchargement pré-authentifié), jamais en <img> vers SharePoint (refusé
// sur iPhone) ; gardé en data URL en mémoire et dans le localStorage — un
// identifiant ne change jamais de contenu, la copie ne se périme pas.

import { GraphApiError, graphFetch, graphGet } from './graphClient'

const DRIVE_ID = import.meta.env.VITE_DRIVE_PIECES_JOINTES_ID ?? ''
const DOSSIER_LOGOS = '_SCE Issues/Logos des sociétés'
const COTE_MAX = 256
const CLE_CACHE = 'sce-issues:logo-societe:'

export const TYPES_LOGO = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml', 'image/gif']
export const POIDS_MAX_LOGO = 5 * 1024 * 1024

/** Ce qui ne va pas avec ce fichier ; null s'il convient. */
export function erreurFichierLogo(f: File): string | null {
  if (!TYPES_LOGO.includes(f.type)) return 'Le logo doit être une image PNG, JPG, WebP, SVG ou GIF.'
  if (f.size > POIDS_MAX_LOGO) return 'Le logo dépasse 5 Mo.'
  return null
}

function chargerImage(f: File): Promise<HTMLImageElement> {
  return new Promise((resoudre, rejeter) => {
    const url = URL.createObjectURL(f)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resoudre(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      rejeter(new Error('Cette image n’a pas pu être lue.'))
    }
    img.src = url
  })
}

/** L'image ramenée à 256 px au plus (proportions gardées), en PNG. */
async function reduire(f: File): Promise<Blob> {
  const img = await chargerImage(f)
  // Un SVG sans taille propre se dessine au plafond.
  const l = img.naturalWidth || COTE_MAX
  const h = img.naturalHeight || COTE_MAX
  const echelle = Math.min(1, COTE_MAX / Math.max(l, h))
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(l * echelle))
  canvas.height = Math.max(1, Math.round(h * echelle))
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Cette image n’a pas pu être préparée.')
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
  return new Promise((resoudre, rejeter) =>
    canvas.toBlob((b) => (b ? resoudre(b) : rejeter(new Error('Cette image n’a pas pu être préparée.'))), 'image/png'),
  )
}

function nomFichier(societe: string): string {
  const net = societe
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()
  return `${net || 'societe'}-${Date.now()}.png`
}

function lireCommeDataUrl(b: Blob): Promise<string> {
  return new Promise((resoudre, rejeter) => {
    const lecteur = new FileReader()
    lecteur.onload = () => resoudre(String(lecteur.result))
    lecteur.onerror = () => rejeter(new Error('Logo illisible'))
    lecteur.readAsDataURL(b)
  })
}

const enMemoire = new Map<string, Promise<string | null>>()

function garder(id: string, dataUrl: string): void {
  enMemoire.set(id, Promise.resolve(dataUrl))
  try {
    localStorage.setItem(CLE_CACHE + id, dataUrl)
  } catch {
    // Stockage plein ou bloqué : la copie en mémoire suffit pour la session.
  }
}

/** Réduit et dépose le logo ; renvoie l'identifiant du fichier, à ranger sur la société. */
export async function envoyerLogo(societe: string, f: File): Promise<string> {
  const png = await reduire(f)
  const chemin = `${DOSSIER_LOGOS}/${nomFichier(societe)}`.split('/').map(encodeURIComponent).join('/')
  const element = await graphFetch<{ id: string }>(`/drives/${DRIVE_ID}/root:/${chemin}:/content?@microsoft.graph.conflictBehavior=rename`, {
    method: 'PUT',
    rawBody: png,
    headers: { 'Content-Type': 'image/png' },
  })
  garder(element.id, await lireCommeDataUrl(png))
  return element.id
}

async function telecharger(id: string): Promise<string | null> {
  try {
    const local = localStorage.getItem(CLE_CACHE + id)
    if (local) return local
  } catch {
    // Pas de stockage : on relit par Graph.
  }
  try {
    const element = await graphGet<{ '@microsoft.graph.downloadUrl'?: string }>(`/drives/${DRIVE_ID}/items/${encodeURIComponent(id)}`)
    const url = element['@microsoft.graph.downloadUrl']
    if (!url) return null
    const reponse = await fetch(url)
    if (!reponse.ok) throw new GraphApiError(`Lecture du logo (${reponse.status})`, reponse.status)
    const dataUrl = await lireCommeDataUrl(await reponse.blob())
    garder(id, dataUrl)
    return dataUrl
  } catch {
    // Logo supprimé de la bibliothèque, ou pas d'accès : le nom seul suffit.
    return null
  }
}

/** Le logo en data URL, ou null (pas de logo, ou illisible). Une lecture par identifiant. */
export function lireLogo(id: string): Promise<string | null> {
  let p = enMemoire.get(id)
  if (!p) {
    p = telecharger(id)
    enMemoire.set(id, p)
  }
  return p
}
